import React, { useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Skeleton, Chip, LinearProgress, IconButton, Paper, TablePagination, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment, TableSortLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardResetButtonSx } from '../../../components/common/tableStyles';
import { InventoryService } from '../../../api/services/inventoryService';

export default function StockCatalogueTab({
    refreshKey = 0,
    setInventoryItemModalOpen,
    handleViewInventoryItem,
    handleOpenEditInventoryItem,
    handleOpenDeleteInventoryItem
}) {
    const [inventoryItems, setInventoryItems] = useState([]);
    const [inventoryItemsLoading, setInventoryItemsLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const [orderBy, setOrderBy] = useState('lastUpdated');
    const [order, setOrder] = useState('desc');

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const fetchInventoryItems = async () => {
        setInventoryItemsLoading(true);
        try {
            const params = {
                page,
                limit: rowsPerPage,
                ...(searchTerm && { search: searchTerm }),
                ...(filterCategory !== 'All' && { category: filterCategory }),
                ...(filterStatus !== 'All' && { status: filterStatus }),
                ...(orderBy && { sortBy: orderBy, sortOrder: order })
            };
            const data = await InventoryService.getInventoryItems(params);
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setInventoryItems(dataArray);
            setTotalItems(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error("Failed to fetch paginated inventory items:", error);
        } finally {
            setInventoryItemsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchInventoryItems();
    }, [page, rowsPerPage, searchTerm, filterCategory, filterStatus, orderBy, order, refreshKey]);

    // Client-side filtering and sorting is now done on the server.

    // Filter Logic
    // Server-side pagination is now used.

    return (
        <Box>
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search items or SKU..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={standardSearchInputSx}
                />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select 
                            value={filterCategory} 
                            onChange={(e) => setFilterCategory(e.target.value)} 
                            displayEmpty
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Categories</MenuItem>
                            <MenuItem value="F&B">F&B</MenuItem>
                            <MenuItem value="Room Supplies">Room Supplies</MenuItem>
                            <MenuItem value="Housekeeping">Housekeeping</MenuItem>
                            <MenuItem value="Maintenance">Maintenance</MenuItem>
                            <MenuItem value="General">General</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select 
                            value={filterStatus} 
                            onChange={(e) => setFilterStatus(e.target.value)} 
                            displayEmpty
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Low Stock">Low Stock</MenuItem>
                            <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                        </Select>
                    </FormControl>
                    {(searchTerm !== '' || filterCategory !== 'All' || filterStatus !== 'All') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            sx={standardResetButtonSx}
                            onClick={() => { setSearchTerm(''); setFilterCategory('All'); setFilterStatus('All'); setPage(0); }}
                        >
                            Reset
                        </Button>
                    )}
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setInventoryItemModalOpen(true)}
                    >
                        Create New Item
                    </Button>
                </Box>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Item Name / SKU</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Unit</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'stockLevel'}
                                    direction={orderBy === 'stockLevel' ? order : 'asc'}
                                    onClick={() => handleRequestSort('stockLevel')}
                                >
                                    Stock Level
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Reorder Level</TableCell>
                            <TableCell>Max Stock</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'lastUpdated'}
                                    direction={orderBy === 'lastUpdated' ? order : 'desc'}
                                    onClick={() => handleRequestSort('lastUpdated')}
                                >
                                    Last Updated
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {inventoryItemsLoading ? (
                            Array.from(new Array(5)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="70%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="30%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell align="right"><Skeleton width={60} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : inventoryItems.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No inventory items found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            inventoryItems.map((item) => (
                                <TableRow 
                                    key={item.id} 
                                    hover
                                    onClick={() => handleViewInventoryItem(item.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{item.name || item.sku || 'Unknown Item'}</Typography>
                                        {item.sku && <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.category || 'General'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.unitOfMeasure || 'pc'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight="bold">{item.currentStock !== undefined ? item.currentStock : (item.stockQuantity || item.stock || 0)}</Typography>
                                            <Chip 
                                                label={item.outOfStock ? "Empty" : item.lowStockWarning ? "Low" : "OK"} 
                                                size="small" 
                                                color={item.outOfStock ? "error" : item.lowStockWarning ? "warning" : "success"}
                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                            />
                                        </Box>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={Math.min((((item.currentStock !== undefined ? item.currentStock : (item.stockQuantity || item.stock || 0))) / Math.max(item.maxStock || (item.reorderLevel || 0) * 2 || 100)) * 100, 100)} 
                                            color={item.outOfStock ? "error" : item.lowStockWarning ? "warning" : "success"}
                                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                        />
                                    </TableCell>
                                    <TableCell>{item.reorderLevel || 0}</TableCell>
                                    <TableCell>{item.maxStock || 0}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={item.status || 'Active'} 
                                            size="small" 
                                            color={(item.status || 'Active').toLowerCase() === 'active' ? 'success' : 'default'}
                                            variant="outlined" 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.updatedAt || item.createdAt ? new Date(item.updatedAt || item.createdAt).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                            <IconButton 
                                                size="small" 
                                                color="primary" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenEditInventoryItem(item);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                color="info" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewInventoryItem(item.id);
                                                }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenDeleteInventoryItem(item.id);
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={inventoryItems.length === 0 ? 0 : totalItems}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
